import { useQuery } from "@tanstack/react-query";

import { useCalcWorker, useDebugOptions } from "./hooks";
import type { ReplicateStats } from "../lib/grid";
import { Select, Toggle } from "../../ui";

type WorkerDiagnostics = Awaited<ReturnType<ReturnType<typeof useCalcWorker>["api"]["getWorkerDiagnostics"]>>;
type ReplicationDiagnostics = NonNullable<WorkerDiagnostics["grid"]["replication"]> & {
  ownId: string;
  version: number;
  peers: { id: string; version: number; isPendingSnapshot: boolean; lag: number }[];
  pendingSnapshotCount: number;
};

export const WorkerDebugStats = () => {
  const worker = useCalcWorker();

  const stats = useQuery({
    queryKey: ["workerStats"],
    queryFn: async () => {
      const [price, eod, diagnostics] = await Promise.all([
        worker.api.getLatestLivePrice(),
        worker.api.getPriorEodDate(),
        worker.api.getWorkerDiagnostics(),
      ]);
      const mainGrid = { ...worker.grid.stats };
      const mainReplication = worker.grid.getDiagnostics();
      return { latestLivePrice: price, priorEodDate: eod, diagnostics, mainGrid, mainReplication };
    },
    refetchInterval: 1000,
  });

  const diag = stats.data?.diagnostics;
  const engineStats = diag?.engine ?? {};

  return (
    <div className="text-[11px] w-full overflow-y-auto max-h-full pb-4 scrollbar-thin">
      {/* Top row: Worker info, State, Cache, Optimistic */}
      <div className="grid grid-cols-2 gap-4">
        <StatsSection title="Worker">
          <Row label="Type" value={<WorkerTypeSelect />} />
          <Row label="Log traces" value={<LogTracesToggle />} />
          <Row label="Status" value={stats.isLoading ? "Connecting..." : "Connected"} />
          <Row label="Wasm Memory" value={formatBytes(diag?.wasmMemory ?? 0)} />
        </StatsSection>

        <StatsSection title="State">
          {diag?.memory && <Row label="Memory usage" value={formatBytes(diag.memory)} />}
          <Row
            label="Last Price"
            value={stats.data?.latestLivePrice ? new Date(stats.data.latestLivePrice).toLocaleTimeString() : "—"}
          />
          <Row label="Prior EOD" value={stats.data?.priorEodDate ?? "—"} />
        </StatsSection>

        <StatsSection title="Cache Sizes">
          <Row label="EOD Dates" value={diag?.cache.eodDates ?? 0} />
          <Row label="Manual Entries" value={diag?.cache.manualEntries ?? 0} />
          <Row label="Edit Metadata" value={diag?.cache.editedMetadata ?? 0} />
        </StatsSection>

        <StatsSection title="Optimistic Queue">
          <Row label="Queue Depth" value={diag?.optimistic?.syncer.queueDepth ?? 0} />
          <Row label="Pending" value={diag?.optimistic?.syncer.pendingCount ?? 0} />
          <Row label="Stash Size" value={diag?.optimistic?.adapter.stashSize ?? 0} />
        </StatsSection>

        <StatsSection title="Engine (Wasm)">
          {Object.entries(engineStats).map(([key, stat]) => (
            <Row key={key} label={key} value={stat?.mean != null ? `${stat.mean.toFixed(1)}ms` : "—"} />
          ))}
        </StatsSection>
      </div>

      {/* Replication Topology */}
      <div className="flex flex-col gap-4 pt-4">
        <ReplicationPanel
          title="Worker Grid Replication"
          replication={diag?.grid.replication as ReplicationDiagnostics}
        />
        <ReplicationPanel
          title="Main Grid Replication"
          replication={stats.data?.mainReplication as ReplicationDiagnostics}
        />
      </div>

      {/* Grid stats: side by side comparison */}
      <div className="flex flex-col gap-4 pt-4">
        <GridStatsPanel title="Worker Grid" stats={diag?.grid.stats} />
        <GridStatsPanel title="Main Grid" stats={stats.data?.mainGrid} />
      </div>
    </div>
  );
};

const ReplicationPanel = ({
  title,
  replication,
}: {
  title: string;
  replication: ReplicationDiagnostics | null | undefined;
}) => {
  if (!replication) {
    return (
      <div className="flex flex-col gap-3 p-3 rounded-md border border-divider bg-surface-1/50">
        <h3 className="text-[11px] font-semibold">{title}</h3>
        <span className="text-foreground-muted">Not available (non-replicated grid)</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-3 rounded-md border border-divider bg-surface-1/50">
      <h3 className="text-[11px] font-semibold">{title}</h3>

      <div className="grid grid-cols-2 gap-4">
        <StatsSection title="Node Info">
          <Row label="Node ID" value={replication.ownId.slice(0, 12)} />
          <Row label="Version" value={replication.version} />
          <Row label="Pending Snapshots" value={replication.pendingSnapshotCount} />
        </StatsSection>

        <StatsSection title="Peers">
          {replication.peers.length === 0 ? (
            <span className="text-foreground-muted">No peers connected</span>
          ) : (
            replication.peers.map((peer) => (
              <Row
                key={peer.id}
                label={peer.id.slice(0, 8)}
                value={
                  <span className={peer.lag > 10 ? "text-warning" : ""}>
                    v{peer.version} {peer.isPendingSnapshot && "(syncing)"} {peer.lag > 0 && `lag:${peer.lag}`}
                  </span>
                }
              />
            ))
          )}
        </StatsSection>
      </div>
    </div>
  );
};

const GridStatsPanel = ({ title, stats }: { title: string; stats: ReplicateStats | null | undefined }) => {
  const totalOps = (stats?.ops_local ?? 0) + (stats?.ops_remote ?? 0);
  const totalBytes = (stats?.bytes_sent ?? 0) + (stats?.bytes_received ?? 0);

  return (
    <div className="flex flex-col gap-3 p-3 rounded-md border border-divider bg-surface-1/50">
      <h3 className="text-[11px] font-semibold">{title}</h3>

      <div className="grid grid-cols-2 gap-4">
        <StatsSection title="Operations">
          <Row label="Local" value={stats?.ops_local ?? 0} />
          <Row label="Remote" value={stats?.ops_remote ?? 0} />
          <Row label="Total" value={totalOps} />
        </StatsSection>

        <StatsSection title="Network">
          <Row label="In" value={formatBytes(stats?.bytes_received ?? 0)} />
          <Row label="Out" value={formatBytes(stats?.bytes_sent ?? 0)} />
          <Row label="Total" value={formatBytes(totalBytes)} />
        </StatsSection>

        <StatsSection title="Encode / Decode">
          <Row label="Encode" value={formatMs(stats?.encode_time)} />
          <Row label="Decode" value={formatMs(stats?.decode_time)} />
          <Row label="Snapshot" value={formatMs(stats?.snapshot_time)} />
        </StatsSection>

        <StatsSection title="Apply Time">
          <Row label="Local" value={formatMs(stats?.apply_time_local)} />
          <Row label="Remote" value={formatMs(stats?.apply_time_remote)} />
        </StatsSection>
      </div>
    </div>
  );
};

const WorkerTypeSelect = () => {
  const [debugOptions, setDebugOptions] = useDebugOptions();

  const options = [
    { value: "dedicated", label: "Dedicated" },
    { value: "shared", label: "Shared" },
  ];

  return (
    <Select
      value={debugOptions.force_normal_worker ? "dedicated" : "shared"}
      onChange={(e) => setDebugOptions({ ...debugOptions, force_normal_worker: e.target.value === "dedicated" })}
      options={options}
    />
  );
};

const LogTracesToggle = () => {
  const [debugOptions, setDebugOptions] = useDebugOptions();
  return (
    <Toggle
      checked={debugOptions.log_traces}
      onChange={(e) => setDebugOptions({ ...debugOptions, log_traces: e.target.checked })}
      aria-label="Log live-price traces to the console"
    />
  );
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatMs = (ms: number | undefined) => `${(ms ?? 0).toFixed(1)}ms`;

const StatsSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <h4 className="text-[10px] font-medium text-foreground-muted uppercase tracking-wide">{title}</h4>
    <div className="flex flex-col gap-0.5">{children}</div>
  </div>
);

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex justify-between items-center gap-4">
    <span className="text-foreground-muted text-nowrap truncate">{label}</span>
    <span className="font-medium tabular-nums">{value}</span>
  </div>
);
